import { z } from "zod";
import { QueryIRSchema } from "./queryBuilder";
import {
  CanvasComponentBaseSchema,
  ReportCanvasLayoutSchema,
} from "./reportCanvas";
import { reportNumberFormatSchema } from "./reportNumberFormat";
import { reportTemplateTextSchema } from "./reportTextTemplate";
import { ReportValueFormatSchema } from "./reportValueFormat";
import { ReportAppPathSchema } from "./reportCanvas";

/**
 * Serialized representation of a saved library report.
 *
 * A saved report is two halves that only make sense together:
 *  - `source` — what data to chart: a query-builder {@link QueryIRSchema} recipe
 *               (referenced by catalog field `key` only; the backend compiles it
 *               to SQL), or one Worksheet of a Workbook, charted from its newest
 *               Current Result.
 *  - `report` — the visual layout built on top of the source's *result columns*
 *               (how to chart it). Chart field references (`xAxisKey`, …) point
 *               at result-column keys (a Worksheet's Output column names).
 *
 * The whole {@link ProcSavedReportDocument} is persisted as a single JSONB blob
 * (the `report` column of `core_saved_reports`). `schemaVersion` lets us migrate
 * old blobs as this shape evolves.
 *
 * This module is the single source of truth for the builtin chart component
 * shapes: the generic report canvas (`frontend/src/components/report-canvas/`)
 * imports these types rather than mirroring them. Each chart schema extends
 * {@link CanvasComponentBaseSchema}, so a component validated here also
 * satisfies the canvas's open persisted-component schema (`./reportCanvas.ts`).
 */

// ─── Version ─────────────────────────────────────────────────────────────────

/**
 * Bump on any non-additive change to {@link ProcSavedReportDocumentSchema}.
 * 2: `query` moved into the tagged `source`.
 * 3: `report` moved from rows of components to the Report grid (a version 2
 *    document's rows still parse; the canvas converts them on read).
 */
export const CURRENT_PROC_SAVED_REPORT_SCHEMA_VERSION = 3;

// ─── Primitives ──────────────────────────────────────────────────────────────

/** A hex color, e.g. "#8b5cf6" or "#abc". */
const HexColorSchema = z
  .string()
  .regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, "Must be a hex color");

/**
 * A chart axis / series reference: a *result-column key* (a select field key, or
 * an aggregate / date-bucket / relation-column alias). `null` = not yet chosen.
 * Existence is resolved against live result columns at load time, not here — the
 * query can be edited independently of the report.
 */
const ColumnRefSchema = z.string().nullable();

// ─── Chart components ────────────────────────────────────────────────────────

/**
 * The order a category chart draws its categories in: as they first appear,
 * by value, or by label.
 */
export const CategorySortSchema = z.enum([
  "none",
  "value-desc",
  "value-asc",
  "label-asc",
  "label-desc",
]);
export type CategorySort = z.infer<typeof CategorySortSchema>;

/** The most categories a Top N keeps. */
export const CATEGORY_TOP_N_MAX = 100;

export const BarChartComponentSchema = CanvasComponentBaseSchema.extend({
  type: z.literal("bar-chart"),
  xAxisKey: ColumnRefSchema.default(null),
  yAxisKey: ColumnRefSchema.default(null),
  showGrid: z.boolean().default(true),
  showLabels: z.boolean().default(false),
  sort: CategorySortSchema.default("none"),
  /**
   * Keep only the first N categories after sorting (the Top N by value when
   * sorted by value, highest first); 0 = every category.
   */
  topN: z.number().int().min(0).max(CATEGORY_TOP_N_MAX).default(0),
  /** How the values read on the axis, labels and tooltip; absent = plain Number. */
  valueFormat: reportNumberFormatSchema.optional(),
});
export type BarChartComponent = z.infer<typeof BarChartComponentSchema>;

export const LineChartComponentSchema = CanvasComponentBaseSchema.extend({
  type: z.literal("line-chart"),
  xAxisKey: ColumnRefSchema.default(null),
  yAxisKey: ColumnRefSchema.default(null),
  filled: z.boolean().default(false),
  curve: z.enum(["linear", "monotone", "step"]).default("monotone"),
  showDots: z.boolean().default(true),
  showGrid: z.boolean().default(true),
  sort: CategorySortSchema.default("none"),
  valueFormat: reportNumberFormatSchema.optional(),
});
export type LineChartComponent = z.infer<typeof LineChartComponentSchema>;

export const ScatterChartComponentSchema = CanvasComponentBaseSchema.extend({
  type: z.literal("scatter-chart"),
  xAxisKey: ColumnRefSchema.default(null),
  yAxisKey: ColumnRefSchema.default(null),
  showGrid: z.boolean().default(true),
  /** How the y values read; absent = plain Number. */
  valueFormat: reportNumberFormatSchema.optional(),
});
export type ScatterChartComponent = z.infer<typeof ScatterChartComponentSchema>;

export const PieChartComponentSchema = CanvasComponentBaseSchema.extend({
  type: z.literal("pie-chart"),
  nameKey: ColumnRefSchema.default(null),
  valueKey: ColumnRefSchema.default(null),
  donut: z.boolean().default(false),
  showLabels: z.boolean().default(false),
  /** 0 = unlimited; otherwise top-N by value, rest grouped into "Other". */
  maxSlices: z.number().int().min(0).default(8),
  /** Custom slice colors by index; a slice without one takes its Report palette's. */
  colors: z.array(HexColorSchema).default([]),
  valueFormat: reportNumberFormatSchema.optional(),
});
export type PieChartComponent = z.infer<typeof PieChartComponentSchema>;

export const RadarChartComponentSchema = CanvasComponentBaseSchema.extend({
  type: z.literal("radar-chart"),
  subjectKey: ColumnRefSchema.default(null),
  valueKey: ColumnRefSchema.default(null),
  gridType: z.enum(["polygon", "circle"]).default("polygon"),
  filled: z.boolean().default(true),
  /** 0 = unlimited; otherwise top-N by value, rest grouped into "Other". */
  maxSections: z.number().int().min(0).default(8),
  /** Stroke + fill color for the series; absent = its Report palette's first color. */
  color: HexColorSchema.optional(),
});
export type RadarChartComponent = z.infer<typeof RadarChartComponentSchema>;

/** The most rows a Table component shows. */
export const TABLE_COMPONENT_MAX_ROWS = 1000;

/** The tone a conditional format paints a cell in: DESIGN.md's status signals. */
export const ReportToneSchema = z.enum([
  "positive",
  "negative",
  "warning",
  "neutral",
]);
export type ReportTone = z.infer<typeof ReportToneSchema>;

/**
 * A Table's conditional format, on one column's cells (never a whole row):
 * - `threshold`: cells whose Number compares true to `value` (`between`:
 *   `value` to `value2`, inclusive) take `tone`;
 * - `color-scale`: every cell takes its place between the shown rows' least
 *   and greatest value on the Report palette's sequential ramp.
 */
export const TableConditionalFormatSchema = z.discriminatedUnion("kind", [
  z.object({
    id: z.string().min(1),
    kind: z.literal("threshold"),
    column: z.string().min(1),
    op: z.enum(["gt", "gte", "lt", "lte", "eq", "neq", "between"]),
    value: z.number().finite(),
    value2: z.number().finite().optional(),
    tone: ReportToneSchema,
  }),
  z.object({
    id: z.string().min(1),
    kind: z.literal("color-scale"),
    column: z.string().min(1),
  }),
]);
export type TableConditionalFormat = z.infer<
  typeof TableConditionalFormatSchema
>;

/**
 * A Table column written out: a row field (`key`) or a text template over
 * the row's fields (`text`, e.g. "{keyed} of {receipts}"), with how it reads.
 * A column given only as its key reads with the dataset column's label and
 * format.
 */
export const ReportTableColumnSchema = z
  .strictObject({
    /** The row field the cell shows. */
    key: z.string().min(1).max(100).optional(),
    /** Or a template over the row's fields. */
    text: reportTemplateTextSchema(300).optional(),
    /** Absent: the dataset column's label. */
    label: z.string().max(100).optional(),
    /** Absent: the dataset column's format. */
    format: ReportValueFormatSchema.optional(),
    align: z.enum(["left", "right"]).optional(),
    /** Display labels for the field's values (e.g. payment type codes). */
    valueLabels: z.record(z.string(), z.string().max(100)).optional(),
    /** Shown when the field is empty. */
    nullText: z.string().max(100).optional(),
    /**
     * Makes the cell a link: a path in the app whose `{placeholders}` are row
     * fields, e.g. "/tasks/{taskId}". No link when a field it names is empty.
     */
    href: ReportAppPathSchema.optional(),
    /** A smaller second line under the cell. */
    secondary: z
      .strictObject({
        key: z.string().min(1).max(100),
        format: ReportValueFormatSchema.optional(),
      })
      .optional(),
    /** Clamp long text to two lines, the full text on hover. */
    clamp: z.boolean().optional(),
    /** The column's least width in rem; absent = sized by its content. */
    width: z.number().min(2).max(40).optional(),
  })
  .refine((column) => (column.key == null) !== (column.text == null), {
    message: "A column has either a key or a text template",
  });
export type ReportTableColumn = z.infer<typeof ReportTableColumnSchema>;

export const TableComponentSchema = CanvasComponentBaseSchema.extend({
  type: z.literal("table"),
  /**
   * The columns shown, in order: column keys, or columns written out;
   * empty shows every column.
   */
  columns: z
    .array(z.union([z.string(), ReportTableColumnSchema]))
    .max(50)
    .default([]),
  /** The leading rows shown. */
  maxRows: z.number().int().min(1).max(TABLE_COMPONENT_MAX_ROWS).default(100),
  /** Number formats by column name; a column without one reads as plain text. */
  columnFormats: z.record(z.string(), reportNumberFormatSchema).default({}),
  /** Applied in order; a later threshold's tone wins on a cell two match. */
  conditionalFormats: z.array(TableConditionalFormatSchema).max(20).default([]),
  /** Rows shown before "Show all"; absent = every row up to `maxRows`. */
  previewRows: z.int().min(1).max(TABLE_COMPONENT_MAX_ROWS).optional(),
  /** Hide the column headers (a compact list). */
  hideHeader: z.boolean().optional(),
  /**
   * Order rows by group: groups by their summed `by` (largest first, an
   * empty group last), then rows within a group by `by`. Absent = as the
   * dataset has them.
   */
  rowOrder: z
    .strictObject({
      group: z.string().min(1).max(100),
      by: z.string().min(1).max(100),
    })
    .optional(),
});
export type TableComponent = z.infer<typeof TableComponentSchema>;

/** How a KPI or Server aggregation reduces a column's values to one. */
export const ReportAggregateFunctionSchema = z.enum([
  "sum",
  "avg",
  "min",
  "max",
  "count",
]);
export type ReportAggregateFunction = z.infer<
  typeof ReportAggregateFunctionSchema
>;

/**
 * A KPI (scorecard): one column's values reduced to a number, optionally
 * compared with a target or another column reduced the same way (Budget,
 * Prior year), showing the change and whether it is good.
 */
export const KpiComponentSchema = CanvasComponentBaseSchema.extend({
  type: z.literal("kpi"),
  valueKey: ColumnRefSchema.default(null),
  fn: ReportAggregateFunctionSchema.default("sum"),
  format: reportNumberFormatSchema.optional(),
  comparison: z
    .discriminatedUnion("kind", [
      z.object({ kind: z.literal("none") }),
      z.object({ kind: z.literal("target"), value: z.number().finite() }),
      z.object({ kind: z.literal("column"), column: ColumnRefSchema }),
    ])
    .default({ kind: "none" }),
  /** An increase is good (sales) or bad (costs, days overdue). */
  higherIsBetter: z.boolean().default(true),
  /**
   * The headline as text over the dataset's totals, e.g. "{share}" or
   * "{days} {days, plural, one {day} other {days}}", in place of `valueKey`
   * reduced by `fn`. Shows "—" when a value it reads is empty.
   */
  value: reportTemplateTextSchema(200).optional(),
  /** A line under the headline; may hold `{placeholders}`. */
  subtext: reportTemplateTextSchema(500).optional(),
});
export type KpiComponent = z.infer<typeof KpiComponentSchema>;

/** The longest a Text block's Markdown may be. */
export const TEXT_COMPONENT_MAX_LENGTH = 10_000;

/** A Text block: Markdown (headings, lists, links, emphasis) shown as written; it reads no dataset. */
export const TextComponentSchema = CanvasComponentBaseSchema.extend({
  type: z.literal("text"),
  markdown: z.string().max(TEXT_COMPONENT_MAX_LENGTH).default(""),
});
export type TextComponent = z.infer<typeof TextComponentSchema>;

/** The grammars a custom chart's spec is written in. */
export const VEGA_CHART_GRAMMARS = ["vega-lite", "vega"] as const;

/**
 * A custom chart: a Vega-Lite (or full Vega) spec as plain JSON, drawn over
 * its bound dataset's rows (the spec never embeds data).
 */
export const VegaChartComponentSchema = CanvasComponentBaseSchema.extend({
  type: z.literal("vega-chart"),
  /** Configs saved before full Vega have none: Vega-Lite. */
  grammar: z.enum(VEGA_CHART_GRAMMARS).default("vega-lite"),
  /** The spec; null until configured. */
  spec: z.record(z.string(), z.unknown()).nullable().default(null),
  /**
   * The plot's height in pixels, for a chart with a natural height (a list
   * of bars); the cell's spare height then sits below it. Absent = the plot
   * fills the cell.
   */
  plotHeight: z.int().min(40).max(1200).optional(),
});
export type VegaChartComponent = z.infer<typeof VegaChartComponentSchema>;

// ─── Saved-report document (the JSONB blob) ──────────────────────────────────
//
// The `report` half is the OPEN canvas layout ({@link ReportCanvasLayoutSchema}
// in `./reportCanvas.ts`), the same serialization every report-canvas surface
// persists — so a saved report can carry any registered widget (vega-chart,
// future types), not just the builtin charts above. The per-chart schemas here
// remain the source of truth for the builtin chart config shapes (re-exported
// by the canvas), but no longer gate the layout.

/** What a saved report charts: a query's result, or a Workbook Worksheet's Result. */
export const SavedReportSourceSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("query"), query: QueryIRSchema }),
  z.object({
    kind: z.literal("workbook"),
    workbookId: z.uuid(),
    sheetId: z.uuid(),
  }),
]);
export type SavedReportSource = z.infer<typeof SavedReportSourceSchema>;

export const ProcSavedReportDocumentSchema = z.object({
  schemaVersion: z.number().int(),
  source: SavedReportSourceSchema,
  report: ReportCanvasLayoutSchema,
});
export type ProcSavedReportDocument = z.infer<
  typeof ProcSavedReportDocumentSchema
>;
export type ProcSavedReportDocumentInput = z.input<
  typeof ProcSavedReportDocumentSchema
>;

// ─── Serialize / deserialize ─────────────────────────────────────────────────

/**
 * Serialize an in-memory query + report into the persisted document shape,
 * stamping the current schema version. The returned object is validated and
 * ready to store in the `report` JSONB column (Sequelize handles JSON encoding).
 */
export function serializeProcSavedReport(input: {
  source: z.input<typeof SavedReportSourceSchema>;
  report: z.input<typeof ReportCanvasLayoutSchema>;
}): ProcSavedReportDocument {
  return ProcSavedReportDocumentSchema.parse({
    schemaVersion: CURRENT_PROC_SAVED_REPORT_SCHEMA_VERSION,
    source: input.source,
    report: input.report,
  });
}

/**
 * Upgrades a stored blob to the current shape before validation. Version 1
 * documents carry a bare `query`; they read as a query source. Stored blobs are
 * never rewritten in place: a document converges when it is next saved.
 */
function upgradeProcSavedReport(raw: unknown): unknown {
  if (typeof raw !== "object" || raw === null || "source" in raw) return raw;
  if (!("query" in raw)) return raw;
  const { query, ...rest } = raw;
  return {
    ...rest,
    // Its `report` is still rows of components, as version 2 saved them.
    schemaVersion: 2,
    source: { kind: "query", query },
  };
}

/**
 * Deserialize a stored JSONB blob back into a typed document. Throws on an
 * invalid blob. This is the single seam for migrations: older
 * `schemaVersion`s are upgraded here before validating.
 */
export function parseProcSavedReport(raw: unknown): ProcSavedReportDocument {
  return ProcSavedReportDocumentSchema.parse(upgradeProcSavedReport(raw));
}

/** Non-throwing variant; returns a Zod SafeParseReturnType. */
export function safeParseProcSavedReport(raw: unknown) {
  return ProcSavedReportDocumentSchema.safeParse(upgradeProcSavedReport(raw));
}
